import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
const CookiePolicy = () => {
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-background text-foreground">
      <SEO title="Cookie Policy | Klexu" description="How Klexu uses cookies and how you can manage your cookie preferences." path="/cookie-policy" />
      <Navbar />
      <main className="pt-24 pb-20">
        <div className="container px-4 sm:px-6 lg:px-8 mx-auto">
          <Button variant="ghost" className="mb-8 text-muted-foreground hover:text-white" onClick={() => window.history.back()}>
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Cookie Policy</h1>
          
          <div className="glass-card p-8 rounded-xl">
            <div className="max-w-full space-y-8 text-justify">
              <div>
                <p className="text-sm text-muted-foreground mb-6">
                  <strong>Version:</strong> 2.1 | <strong>Last Updated:</strong> December 3, 2025 | 
                  <strong>Previous Version:</strong> December 24, 2024 (available upon request)
                </p>
                <p className="text-muted-foreground mb-4">
                  This Cookie Policy explains how Klexu Resources SRL ("we", "us", or "our") uses cookies and similar 
                  technologies on our website.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-bold mb-3">What Are Cookies</h2>
                <p className="text-muted-foreground mb-4">
                  Cookies are small text files that are stored on your device when you visit a website. 
                  They are widely used to make websites work more efficiently, provide information to 
                  website owners, and improve user experience.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-bold mb-3">How We Use Cookies</h2>
                <p className="text-muted-foreground mb-4">
                  We use cookies for various purposes, including:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Essential cookies: necessary for the website to function properly</li>
                  <li>Performance cookies: to analyze how visitors use our website</li>
                  <li>Functionality cookies: to remember your preferences</li>
                  <li>Targeting cookies: to deliver more relevant advertisements</li>
                </ul>
              </div>
              
              <div>
                <h2 className="text-xl font-bold mb-3">Managing Cookies</h2>
                <p className="text-muted-foreground mb-4">
                  Most web browsers allow you to control cookies through their settings. You can usually find 
                  these settings in the "Options" or "Preferences" menu of your browser. However, if you disable 
                  certain cookies, you may not be able to use all features of our website.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-bold mb-4">Complete Cookie Inventory</h2>
                <p className="text-muted-foreground mb-4">
                  Below is a complete list of cookies used on our website:
                </p>

                <div className="space-y-6">
                  <div className="border border-border rounded-lg overflow-hidden">
                    <div className="bg-muted/50 p-3 border-b border-border">
                      <h3 className="font-semibold">Strictly Necessary Cookies (Cannot be disabled)</h3>
                    </div>
                    <div className="p-4 space-y-4">
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium mb-1">sb-access-token</p>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p><strong>Purpose:</strong> User authentication and session management</p>
                          <p><strong>Provider:</strong> Supabase (First-party)</p>
                          <p><strong>Duration:</strong> Session (deleted when browser closes)</p>
                          <p><strong>Type:</strong> HTTP Cookie</p>
                        </div>
                      </div>
                      
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium mb-1">sb-refresh-token</p>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p><strong>Purpose:</strong> Maintain user login across sessions</p>
                          <p><strong>Provider:</strong> Supabase (First-party)</p>
                          <p><strong>Duration:</strong> 30 days</p>
                          <p><strong>Type:</strong> HTTP Cookie</p>
                        </div>
                      </div>
                      
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium mb-1">cookie-consent</p>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p><strong>Purpose:</strong> Store user's cookie preferences</p>
                          <p><strong>Provider:</strong> Klexu (First-party)</p>
                          <p><strong>Duration:</strong> 365 days</p>
                          <p><strong>Type:</strong> LocalStorage</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border border-border rounded-lg overflow-hidden">
                    <div className="bg-muted/50 p-3 border-b border-border">
                      <h3 className="font-semibold">Analytics Cookies (Requires consent)</h3>
                    </div>
                    <div className="p-4 space-y-4">
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium mb-1">_ga</p>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p><strong>Purpose:</strong> Distinguish unique users for analytics</p>
                          <p><strong>Provider:</strong> Google Analytics (Third-party)</p>
                          <p><strong>Duration:</strong> 24 months</p>
                          <p><strong>Legal Basis:</strong> Consent</p>
                        </div>
                      </div>
                      
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium mb-1">_ga_[container-id]</p>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p><strong>Purpose:</strong> Persist session state</p>
                          <p><strong>Provider:</strong> Google Analytics (Third-party)</p>
                          <p><strong>Duration:</strong> 24 months</p>
                          <p><strong>Legal Basis:</strong> Consent</p>
                        </div>
                      </div>
                      
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium mb-1">_clck</p>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p><strong>Purpose:</strong> Session tracking for heatmaps</p>
                          <p><strong>Provider:</strong> Microsoft Clarity (Third-party)</p>
                          <p><strong>Duration:</strong> 12 months</p>
                          <p><strong>Legal Basis:</strong> Consent</p>
                        </div>
                      </div>
                      
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium mb-1">_clsk</p>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p><strong>Purpose:</strong> Connect multiple page views</p>
                          <p><strong>Provider:</strong> Microsoft Clarity (Third-party)</p>
                          <p><strong>Duration:</strong> 1 day</p>
                          <p><strong>Legal Basis:</strong> Consent</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border border-border rounded-lg overflow-hidden">
                    <div className="bg-muted/50 p-3 border-b border-border">
                      <h3 className="font-semibold">Marketing Cookies (Requires consent)</h3>
                    </div>
                    <div className="p-4 space-y-4">
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium mb-1">_gcl_au</p>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p><strong>Purpose:</strong> Store and track conversions</p>
                          <p><strong>Provider:</strong> Google Ads (Third-party)</p>
                          <p><strong>Duration:</strong> 90 days</p>
                          <p><strong>Legal Basis:</strong> Consent</p>
                        </div>
                      </div>
                      
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium mb-1">__hstc</p>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p><strong>Purpose:</strong> Track visitors across sessions</p>
                          <p><strong>Provider:</strong> HubSpot (Third-party)</p>
                          <p><strong>Duration:</strong> 13 months</p>
                          <p><strong>Legal Basis:</strong> Consent</p>
                        </div>
                      </div>
                      
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium mb-1">hubspotutk</p>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p><strong>Purpose:</strong> Track visitor identity</p>
                          <p><strong>Provider:</strong> HubSpot (Third-party)</p>
                          <p><strong>Duration:</strong> 13 months</p>
                          <p><strong>Legal Basis:</strong> Consent</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border border-border rounded-lg overflow-hidden">
                    <div className="bg-muted/50 p-3 border-b border-border">
                      <h3 className="font-semibold">Preferences Cookies (Requires consent)</h3>
                    </div>
                    <div className="p-4 space-y-4">
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium mb-1">theme-preference</p>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p><strong>Purpose:</strong> Remember dark/light mode preference</p>
                          <p><strong>Provider:</strong> Klexu (First-party)</p>
                          <p><strong>Duration:</strong> 365 days</p>
                          <p><strong>Legal Basis:</strong> Legitimate Interest</p>
                        </div>
                      </div>
                      
                      <div className="border-l-2 border-primary/50 pl-3">
                        <p className="font-medium mb-1">language-preference</p>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p><strong>Purpose:</strong> Remember selected language</p>
                          <p><strong>Provider:</strong> Klexu (First-party)</p>
                          <p><strong>Duration:</strong> 365 days</p>
                          <p><strong>Legal Basis:</strong> Legitimate Interest</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 border border-border rounded-lg bg-muted/30">
                  <h4 className="font-semibold mb-2">Third-Party Cookie Disclosure</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Some third-party services may set additional cookies. We have no control over these cookies. 
                    Please review the privacy policies of our third-party providers:
                  </p>
                  <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                    <li>Google: <a href="https://policies.google.com/technologies/cookies" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">https://policies.google.com/technologies/cookies</a></li>
                    <li>Microsoft: <a href="https://privacy.microsoft.com/en-us/privacystatement" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">https://privacy.microsoft.com/en-us/privacystatement</a></li>
                    <li>HubSpot: <a href="https://legal.hubspot.com/cookie-policy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">https://legal.hubspot.com/cookie-policy</a></li>
                  </ul>
                </div>
              </div>
              
              <div>
                <h2 className="text-xl font-bold mb-4">Managing Your Cookie Preferences</h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2">Cookie Consent Manager</h3>
                    <p className="text-muted-foreground mb-2">
                      You can manage your cookie preferences at any time using our Cookie Consent Manager:
                    </p>
                    <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                      <li>Click the "Cookie Settings" link in the website footer</li>
                      <li>Or visit your Profile Settings → Privacy & Data → Cookie Preferences</li>
                      <li>Changes take effect immediately</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Browser Settings</h3>
                    <p className="text-muted-foreground mb-2">
                      You can also control cookies through your browser settings:
                    </p>
                    <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                      <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies</li>
                      <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies</li>
                      <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
                      <li><strong>Edge:</strong> Settings → Privacy → Cookies</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Impact of Disabling Cookies</h3>
                    <p className="text-muted-foreground mb-2">
                      Disabling certain cookies may affect functionality:
                    </p>
                    <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                      <li><strong>Essential cookies:</strong> Cannot be disabled (required for basic functionality)</li>
                      <li><strong>Analytics cookies:</strong> Website performance insights will not be collected</li>
                      <li><strong>Marketing cookies:</strong> You will still see ads, but they won't be personalized</li>
                      <li><strong>Preferences cookies:</strong> Your settings will not be saved across sessions</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Withdrawing Consent</h3>
                    <p className="text-muted-foreground mb-2">
                      You can withdraw your cookie consent at any time:
                    </p>
                    <ol className="list-decimal pl-6 text-sm text-muted-foreground space-y-1">
                      <li>Open Cookie Consent Manager (footer link)</li>
                      <li>Disable specific cookie categories</li>
                      <li>Click "Save Preferences"</li>
                    </ol>
                    <p className="text-sm text-muted-foreground mt-2">
                      Your choice will be recorded and implemented immediately.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-2">Do Not Track Signals</h3>
                    <p className="text-muted-foreground mb-2">
                      We honor Do Not Track (DNT) browser signals. When DNT is enabled, we:
                    </p>
                    <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                      <li>Disable all non-essential cookies</li>
                      <li>Stop third-party analytics tracking</li>
                      <li>Prevent behavioral advertising</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div>
                <h2 className="text-xl font-bold mb-3">Contact Information</h2>
                <div className="space-y-4">
                  <div>
                    <div className="text-muted-foreground space-y-1">
                      <p>Email: <a href="mailto:admin@klexu.com" className="text-primary hover:underline">admin@klexu.com</a></p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="pt-8 mt-8 border-t border-border">
                <h2 className="text-xl font-bold mb-4">Cookie Policy Changelog</h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2">Version 2.1 (December 3, 2025)</h3>
                    <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                      <li>Updated policy dates and version references</li>
                      <li>Added changelog section</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold mb-2">Version 2.0 (December 24, 2024)</h3>
                    <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                      <li>Complete cookie inventory with detailed purpose descriptions</li>
                      <li>Added GDPR-compliant consent controls</li>
                      <li>Enhanced Do Not Track signal support</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold mb-2">Version 1.0 (August 4, 2024)</h3>
                    <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
                      <li>Initial cookie policy publication</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>;
};
export default CookiePolicy;